import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion } from "framer-motion";
import { Button } from "../../../components/ui/button";
import Footer from "../../../components/landing/Footer";

const Terms: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const sections = [
    {
      title: "1. Acceptance of Terms",
      content: "By accessing or using Social Bunkr's platform, website, or services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.",
    },
    {
      title: "2. Services Description",
      content: "Social Bunkr provides an online platform connecting property owners (Hosts) with individuals seeking accommodation (Guests). We facilitate bookings, payments, and communication between Hosts and Guests. Social Bunkr is not a property owner and does not own, control, or manage any of the listed properties.",
    },
    {
      title: "3. User Accounts",
      content: "To access certain features, you must create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account. You must provide accurate and complete information during registration and keep your information updated.",
    },
    {
      title: "4. Booking & Payments",
      content: "All bookings are subject to Host availability and acceptance. Payments are processed securely via Razorpay. The total payable amount includes the base price plus applicable GST (18%). Once a booking is confirmed, cancellations are subject to our Refund Policy.",
    },
    {
      title: "5. Host Obligations",
      content: "Hosts must provide accurate descriptions, photos, and amenities for their listings. Hosts are responsible for maintaining the property in a safe and habitable condition and complying with all applicable local laws and regulations.",
    },
    {
      title: "6. Guest Obligations",
      content: "Guests must treat the property and its contents with care and respect. Guests are responsible for any damages caused during their stay. Guests must adhere to the property's house rules, check-in/check-out times, and occupancy limits.",
    },
    {
      title: "7. Prohibited Activities",
      content: "You agree not to use the platform for any unlawful purpose, engage in fraudulent activity, impersonate others, scrape or harvest data, or interfere with the platform's security or functionality.",
    },
    {
      title: "8. Limitation of Liability",
      content: "Social Bunkr acts as an intermediary and is not liable for the conduct of Hosts or Guests, property conditions, losses or damages arising from use of the platform, or events beyond our reasonable control.",
    },
    {
      title: "9. Intellectual Property",
      content: "All content on the Social Bunkr platform, including text, graphics, logos, and software, is the property of Social Bunkr or its licensors and is protected by applicable intellectual property laws.",
    },
    {
      title: "10. Changes to Terms",
      content: "Social Bunkr reserves the right to modify these Terms at any time. We will notify users of significant changes via email or a prominent notice on our platform. Continued use of our services after changes constitutes acceptance of the updated Terms.",
    },
    {
      title: "11. Governing Law",
      content: "These Terms are governed by the laws of India. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts in Hyderabad, Telangana.",
    },
    {
      title: "12. Contact",
      content: "For questions about these Terms of Service, contact us at hello@socialbunkr.in or call +91-8008585553.",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border"
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            <motion.div className="flex items-center gap-2" whileHover={{ scale: 1.02 }}>
              <Link to="/" className="text-2xl md:text-3xl font-bold">
                <span className="text-primary">Social</span>
                <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md ml-1">Bunkr</span>
              </Link>
            </motion.div>
            <nav className="hidden md:flex items-center gap-8">
              {["Home", "About", "Contact"].map((item) => (
                <Link key={item} to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                  className="text-foreground/80 hover:text-primary font-medium transition-colors">
                  {item}
                </Link>
              ))}
            </nav>
            <div className="hidden md:flex items-center gap-4">
              <Link to="/login"><Button variant="ghost" className="text-foreground hover:text-primary">Login</Button></Link>
              <Link to="/signup"><Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6">Sign Up</Button></Link>
            </div>
            <button className="md:hidden p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          {isMobileMenuOpen && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden py-4 space-y-3">
              {["Home", "About", "Contact"].map((item) => (
                <Link key={item} to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                  className="block text-foreground/80 hover:text-primary font-medium transition-colors py-2">
                  {item}
                </Link>
              ))}
            </motion.div>
          )}
        </div>
      </motion.header>

      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-4xl font-bold text-foreground mb-2">Terms of Service</h1>
            <p className="text-muted-foreground mb-8">Last updated: January 1, 2025</p>

            <div className="bg-background rounded-2xl shadow-sm p-8 space-y-8">
              <p className="text-foreground/80 leading-relaxed">
                Welcome to Social Bunkr. Please read these Terms of Service carefully before using our platform.
              </p>
              {sections.map((section) => (
                <div key={section.title}>
                  <h2 className="text-lg font-semibold text-foreground mb-2">{section.title}</h2>
                  <p className="text-foreground/70 leading-relaxed">{section.content}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-muted-foreground text-sm mb-4">
                Questions about our Terms? We're happy to help.
              </p>
              <Link to="/contact">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Terms;
